/**
 * Слой совместимости с Telegram (17.6).
 *
 * Правила:
 *  - фичи вызывают только эти функции, к window.Telegram напрямую не ходят;
 *  - каждый вызов нового API защищён проверкой версии;
 *  - если метода нет — тихий no-op или честный fallback, но не падение;
 *  - вне Telegram (обычный браузер, тесты) всё работает в degraded-режиме.
 */

import type { TelegramLocationData, TelegramWebApp } from './types'

export type { TelegramWebApp } from './types'

export interface GeoResult {
  ok: boolean
  lat?: number
  lon?: number
  accuracyM?: number
  capturedAt?: string
  /** Почему не получилось — нужно, чтобы честно сказать это пользователю. */
  reason?: 'unavailable' | 'denied' | 'timeout' | 'error'
}

const GEO_TIMEOUT_MS = 5000

export function webApp(): TelegramWebApp | undefined {
  return typeof window === 'undefined' ? undefined : window.Telegram?.WebApp
}

export function isTelegram(): boolean {
  return Boolean(webApp()?.initData)
}

export function isVersionAtLeast(version: string): boolean {
  const app = webApp()
  if (!app) return false
  try {
    return app.isVersionAtLeast(version)
  } catch {
    return false
  }
}

/** Сырая строка initData для заголовка Authorization. */
export function getInitData(): string {
  return webApp()?.initData ?? ''
}

export function platform(): string {
  return webApp()?.platform ?? 'unknown'
}

/* ─── Инициализация ─────────────────────────────────────────────────────── */

/**
 * Готовим окружение мини-аппа: сообщаем Telegram, что отрисовались,
 * разворачиваем на весь экран и синхронизируем тему и safe-area.
 * Возвращает функцию отписки.
 */
export function initMiniApp(onThemeChange?: (scheme: 'light' | 'dark') => void): () => void {
  // Тема всегда светлая — решение владельца продукта. Тёмная версия дизайна в
  // theme.css остаётся, но не включается ни по Telegram, ни по системе:
  // «тёплая бумага» задумана светлой, и у всех приложение выглядит одинаково.
  document.documentElement.classList.remove('dark')

  const app = webApp()
  if (!app) return () => {}

  app.ready()
  app.expand()

  // Свайп вниз внутри списка не должен закрывать приложение.
  if (isVersionAtLeast('7.7')) app.disableVerticalSwipes?.()

  const applyTheme = () => {
    document.documentElement.classList.remove('dark')
    // Цвет шапки и фона подгоняем под наш фон, а не под тему Telegram. Делаем
    // это и при смене темы в Telegram: он сбрасывает цвет шапки на свой.
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--color-bg').trim()
    if (bg && isVersionAtLeast('6.1')) {
      app.setHeaderColor?.(bg)
      app.setBackgroundColor?.(bg)
    }
    if (bg && isVersionAtLeast('7.10')) app.setBottomBarColor?.(bg)
    onThemeChange?.('light')
  }

  const applySafeArea = () => {
    const root = document.documentElement.style
    const safe = app.safeAreaInset
    const content = app.contentSafeAreaInset
    if (safe) {
      root.setProperty('--safe-top', `${safe.top}px`)
      root.setProperty('--safe-bottom', `${safe.bottom}px`)
    }
    if (content) root.setProperty('--content-safe-top', `${content.top}px`)
  }

  applyTheme()
  applySafeArea()

  app.onEvent('themeChanged', applyTheme)
  app.onEvent('safeAreaChanged', applySafeArea)
  app.onEvent('contentSafeAreaChanged', applySafeArea)

  return () => {
    app.offEvent('themeChanged', applyTheme)
    app.offEvent('safeAreaChanged', applySafeArea)
    app.offEvent('contentSafeAreaChanged', applySafeArea)
  }
}

/* ─── Тактильная отдача ─────────────────────────────────────────────────── */

type HapticKind = 'tap' | 'success' | 'warning' | 'error' | 'select' | 'heavy'

/** Хаптик только на действиях, которые что-то меняют (17.13). */
export function haptic(kind: HapticKind = 'tap'): void {
  const hf = webApp()?.HapticFeedback
  if (!hf || !isVersionAtLeast('6.1')) return
  try {
    switch (kind) {
      case 'success':
        hf.notificationOccurred('success')
        break
      case 'warning':
        hf.notificationOccurred('warning')
        break
      case 'error':
        hf.notificationOccurred('error')
        break
      case 'select':
        hf.selectionChanged()
        break
      case 'heavy':
        hf.impactOccurred('medium')
        break
      default:
        hf.impactOccurred('light')
    }
  } catch {
    /* хаптик — украшение, его отсутствие не должно ломать действие */
  }
}

/* ─── Навигация ─────────────────────────────────────────────────────────── */

export function setBackButton(visible: boolean, onClick?: () => void): () => void {
  const app = webApp()
  if (!app) return () => {}
  const button = app.BackButton
  if (!visible) {
    button.hide()
    return () => {}
  }
  const handler = onClick ?? (() => window.history.back())
  button.onClick(handler)
  button.show()
  return () => {
    button.offClick(handler)
    button.hide()
  }
}

export function openTelegramLink(url: string): void {
  const app = webApp()
  if (app?.openTelegramLink) app.openTelegramLink(url)
  else window.open(url, '_blank', 'noopener')
}

/** Поделиться ссылкой-приглашением через нативный шэринг Telegram. */
export function shareInvite(link: string, text: string): void {
  const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`
  openTelegramLink(url)
}

/**
 * Отправить приглашение карточкой от бота, а не голой ссылкой.
 *
 * switchInlineQuery открывает выбор чата и подставляет «@бот inv_<токен>»,
 * бот отвечает карточкой с кнопкой «Принять приглашение»
 * (backend/bot/handlers/invite_inline.py). Длинная ссылка в сообщении не видна.
 *
 * Если у бота не включён inline-режим, SDK Telegram бросает исключение, а
 * старый клиент метода не знает, — тогда обычное «Поделиться» со ссылкой:
 * приглашение должно уйти в любом случае.
 */
export function shareInviteCard(
  token: string | undefined,
  link: string,
  fallbackText: string,
): void {
  const app = webApp()
  // Без токена карточку не собрать: бот ответил бы на «inv_» пустотой.
  if (token && app?.switchInlineQuery && isVersionAtLeast('6.7')) {
    try {
      app.switchInlineQuery(`inv_${token}`, ['users', 'groups'])
      return
    } catch {
      // inline-режим выключен — ниже обычная отправка ссылкой
    }
  }
  shareInvite(link, fallbackText)
}

/* ─── Прогрессивные улучшения ───────────────────────────────────────────── */

export function requestFullscreenIfSupported(): boolean {
  const app = webApp()
  if (!app?.requestFullscreen || !isVersionAtLeast('8.0')) return false
  app.requestFullscreen()
  return true
}

export function addToHomeScreenIfSupported(): boolean {
  const app = webApp()
  if (!app?.addToHomeScreen || !isVersionAtLeast('8.0')) return false
  app.addToHomeScreen()
  return true
}

export function downloadFileIfSupported(url: string, fileName: string): boolean {
  const app = webApp()
  if (!app?.downloadFile || !isVersionAtLeast('8.0')) return false
  app.downloadFile({ url, file_name: fileName })
  return true
}

/* ─── Геолокация ────────────────────────────────────────────────────────── */

/**
 * Взять одну точку — строго из обработчика нажатия пользователя.
 *
 * Никаких таймеров, никакого фонового опроса: функция вызывается ровно один
 * раз на действие и всегда возвращает результат, даже отрицательный, чтобы
 * вызывающий код мог продолжить работу без точки.
 */
export async function requestLocation(): Promise<GeoResult> {
  const app = webApp()
  const capturedAt = new Date().toISOString()

  if (app?.LocationManager && isVersionAtLeast('8.0')) {
    const manager = app.LocationManager
    const result = await new Promise<GeoResult>((resolve) => {
      const finish = (value: GeoResult) => resolve(value)
      const timer = window.setTimeout(() => finish({ ok: false, reason: 'timeout' }), GEO_TIMEOUT_MS)

      const read = () => {
        if (!manager.isLocationAvailable) {
          window.clearTimeout(timer)
          finish({ ok: false, reason: 'unavailable' })
          return
        }
        manager.getLocation((location: TelegramLocationData | null) => {
          window.clearTimeout(timer)
          if (!location) {
            finish({ ok: false, reason: manager.isAccessGranted ? 'error' : 'denied' })
            return
          }
          finish({
            ok: true,
            lat: location.latitude,
            lon: location.longitude,
            accuracyM:
              location.horizontal_accuracy != null
                ? Math.round(location.horizontal_accuracy)
                : undefined,
            capturedAt,
          })
        })
      }

      if (manager.isInited) read()
      else manager.init(read)
    })
    if (result.ok || result.reason === 'denied') return result
    // LocationManager недоступен на этом клиенте — пробуем браузерный API
  }

  if (!navigator.geolocation) return { ok: false, reason: 'unavailable' }

  return new Promise<GeoResult>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          ok: true,
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracyM: Math.round(position.coords.accuracy),
          capturedAt,
        }),
      (error) =>
        resolve({
          ok: false,
          reason: error.code === error.PERMISSION_DENIED ? 'denied' : 'timeout',
        }),
      { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 0 },
    )
  })
}

/** Открыть системные настройки доступа — когда няня раньше нажала «Запретить». */
export function openLocationSettings(): boolean {
  const manager = webApp()?.LocationManager
  if (!manager || !isVersionAtLeast('8.0')) return false
  manager.openSettings()
  return true
}

/* ─── Диалоги ───────────────────────────────────────────────────────────── */

export function confirmNative(message: string): Promise<boolean> {
  const app = webApp()
  if (!app || !isVersionAtLeast('6.2')) return Promise.resolve(window.confirm(message))
  return new Promise((resolve) => app.showConfirm(message, (ok) => resolve(ok)))
}

/**
 * start_param из подписанной строки initData.
 *
 * Читаем именно из initData, а не из initDataUnsafe: даже для навигации
 * лучше брать значение из строки, чью подпись сервер уже проверил. Сам токен
 * приглашения всё равно валидируется на бэкенде.
 */
export function getStartParam(): string | undefined {
  const raw = getInitData()
  if (!raw) return undefined
  return new URLSearchParams(raw).get('start_param') ?? undefined
}
